import { createFileRoute, Link } from '@tanstack/react-router';
import type { FixedAsset } from '@accounting/shared';
import { useFixedAssets } from '../../hooks/useFixedAssets';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/fixed-assets/')({
  component: FixedAssetsPage,
});

export function assetStatusTag(a: Pick<FixedAsset, 'status'>) {
  if (a.status === 'disposed') return <span className="tag bad">disposed</span>;
  if (a.status === 'fully_depreciated') return <span className="tag">fully depreciated</span>;
  return <span className="tag ok">active</span>;
}

function FixedAssetsPage() {
  const companyId = useActiveCompanyId();
  const { data: assets = [], isLoading, error } = useFixedAssets(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Fixed Assets</h1>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Fixed Assets</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Asset register & depreciation schedules</p>
        </div>
        <Link to="/fixed-assets/new" className="btn-primary" style={{ textDecoration: 'none' }}>+ New asset</Link>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load fixed assets.</p>}

      {!isLoading && assets.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No fixed assets yet.</p>
          <Link to="/fixed-assets/new" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>Add your first asset</Link>
        </div>
      )}

      {assets.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Asset</th>
              <th>In service</th>
              <th style={{ textAlign: 'right' }}>Cost</th>
              <th style={{ textAlign: 'right' }}>Accum. dep.</th>
              <th style={{ textAlign: 'right' }}>Book value</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {assets.map((a) => (
              <tr key={a.id} style={{ opacity: a.status === 'disposed' ? 0.6 : 1 }}>
                <td style={{ fontWeight: 500 }}>{a.name}</td>
                <td>{formatAccountingDate(a.inServiceDate)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(a.cost)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(a.accumulatedDepreciation)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(a.bookValue)}</td>
                <td>{assetStatusTag(a)}</td>
                <td>
                  <Link to="/fixed-assets/$id" params={{ id: a.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>View</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
